import { normalizeLoadingPhaseText } from "./loadingPhaseCatalog";

export const STATUS_ACTIVE = "active";
export const STATUS_DONE = "done";

export const normalizeProcessText = (value) =>
  String(value || "").trim().replace(/\s+/g, " ");

export const truncateText = (value, limit) => {
  const text = String(value || "").trim();
  const effectiveLimit = Math.max(Number(limit) || 0, 10000);
  if (!text || text.length <= effectiveLimit) return text;
  return text.slice(0, effectiveLimit).trim();
};

const compactProcessText = (value, limit = 118) => {
  const text = normalizeProcessText(value);
  if (limit === false) return text;
  return truncateText(text, limit);
};

export const processEventPayload = (event) => {
  if (!event) return {};
  if (event.text && typeof event.text === "object") return event.text;
  if (typeof event.text === "string") return { message: event.text };
  if (typeof event === "object") return event;
  return { message: event };
};

export const normalizeEvidenceItems = (payload) => {
  const raw = payload?.evidence_items || payload?.evidenceItems;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      type: normalizeProcessText(item.type || "evidence"),
      stageKey: normalizeProcessText(item.stage_key || item.stageKey),
      ticker: normalizeProcessText(item.ticker),
      label: normalizeProcessText(item.label),
      value: normalizeProcessText(item.value),
      shortValue: normalizeProcessText(item.short_value || item.shortValue),
      change: normalizeProcessText(item.change),
      detail: normalizeProcessText(item.detail),
      source: normalizeProcessText(item.source),
      asOf: normalizeProcessText(item.as_of || item.asOf),
    }))
    .filter((item) => item.label || item.value || item.detail);
};

export const isProcessContextEvidence = (item) => (
  item?.type === "process_context" || item?.source === "request context"
);

export const evidenceItemsForPreview = (items = []) => {
  const richItems = items.filter((item) => !isProcessContextEvidence(item));
  return richItems.length ? richItems : items;
};

export const evidenceItemKey = (item) => [
  item?.type,
  item?.stageKey,
  item?.ticker,
  item?.label,
  item?.value,
  item?.detail,
].filter(Boolean).join("|");

export const evidenceItemRank = (item) => {
  if (!item) return 0;
  let rank = 0;
  if (item.type && item.type !== "process_context") rank += 4;
  if (item.source && item.source !== "request context") rank += 2;
  if (item.ticker) rank += 1;
  if (item.value) rank += 1;
  if (item.detail && item.detail !== item.value) rank += 1;
  return rank;
};

// A ticker's item is re-reported when its data refreshes; the newer value
// replaces the earlier one in place rather than listing the ticker twice.
const evidenceMergeKey = (item) => (
  item?.ticker ? [item.type, item.stageKey, item.ticker].join("|") : evidenceItemKey(item)
);

export const PREVIEW_EVIDENCE_LIMIT = 8;
// A coverage row carries one item per ticker it covers.
export const COVERAGE_EVIDENCE_LIMIT = 100;

// A sample (preview) leads with its richest items; a coverage row lists every
// ticker in the order the request named them.
export const mergeEvidenceItems = (
  existingItems = [],
  incomingItems = [],
  limit = PREVIEW_EVIDENCE_LIMIT,
  { ranked = true } = {},
) => {
  const byKey = new Map();
  [...existingItems, ...incomingItems].forEach((item, index) => {
    const key = evidenceMergeKey(item) || `item-${index}`;
    const existing = byKey.get(key);
    byKey.set(key, { item, index: existing ? existing.index : index });
  });

  return Array.from(byKey.values())
    .sort((a, b) => {
      const rankDelta = ranked ? evidenceItemRank(b.item) - evidenceItemRank(a.item) : 0;
      return rankDelta || a.index - b.index;
    })
    .map((entry) => entry.item)
    .slice(0, limit);
};

const COVERAGE_COUNT_KEYS = [
  "requested",
  "covered",
  "refreshing",
  "refreshed",
  "missing_count",
  "withheld_count",
  "not_included_count",
  "not_included_limit",
];

const coverageSymbols = (value) => (
  Array.isArray(value) ? value.map((symbol) => normalizeProcessText(symbol)).filter(Boolean) : []
);

// The counts behind a row ("34 of 34"), from LoadingPhases::Coverage.
export const normalizeCoverage = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const requested = Number(raw.requested);
  const covered = Number(raw.covered);
  if (!Number.isFinite(requested) || !Number.isFinite(covered)) return null;

  const coverage = {
    basis: ["checked", "identified"].includes(raw.basis) ? raw.basis : "requested",
    missing: coverageSymbols(raw.missing),
    withheld: coverageSymbols(raw.withheld),
    notIncluded: coverageSymbols(raw.not_included || raw.notIncluded),
    asOfOldest: normalizeProcessText(raw.as_of_oldest || raw.asOfOldest),
    asOfNewest: normalizeProcessText(raw.as_of_newest || raw.asOfNewest),
  };
  COVERAGE_COUNT_KEYS.forEach((key) => {
    const camel = key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());
    const value = Number(raw[key] ?? raw[camel]);
    if (Number.isFinite(value) && value >= 0) coverage[camel] = value;
  });
  coverage.missingCount = Math.max(coverage.missingCount || 0, coverage.missing.length);
  coverage.withheldCount = Math.max(coverage.withheldCount || 0, coverage.withheld.length);
  coverage.notIncludedCount = Math.max(coverage.notIncludedCount || 0, coverage.notIncluded.length);
  return coverage;
};

const evidenceGroupsForStage = (stageKey, evidenceItems) => {
  if (!evidenceItems.length) return [[stageKey, []]];

  const groups = new Map();
  evidenceItems.forEach((item) => {
    const key = item.stageKey || stageKey || "evidence";
    const existing = groups.get(key) || [];
    groups.set(key, [...existing, item]);
  });

  return Array.from(groups.entries());
};

const isGenericProcessMessage = (message) => [
  "generating response",
  "now aggregating all data for analysis",
].includes(normalizeLoadingPhaseText(message));

const stageDisplayLabel = (message, stageKey) => {
  const normalized = normalizeLoadingPhaseText(message);

  if (stageKey === "time_scales" || normalized.startsWith("focusing on time scales")) return "Time scales";
  if (stageKey === "ticker_preplan") return "Tickers identified";
  if (stageKey === "market_indices") return "Market indices";
  if (stageKey === "quotes") return "Prices";
  if (stageKey === "stats") return "Key stats";
  if (stageKey === "short_setup") return "Short setup";
  if (stageKey === "filings") return "SEC filings";
  if (stageKey === "deep_thinking") return "Deep synthesis";
  if (stageKey === "evidence") return "Evidence packet";
  if (stageKey === "news") return "Headlines";
  if (stageKey === "chart_context") return "Technicals";
  if (stageKey === "options") return "Options";
  if (stageKey === "macro") return "Macro data";
  if (stageKey === "direct_data") return "Direct market data";
  if (stageKey === "specialists") return "Specialist analysis";
  if (stageKey === "synthesis") return "Final synthesis";
  if (stageKey === "response") return "Answer assembly";
  if (stageKey === "plan") return "Planning";
  if (normalized.startsWith("engaging agentic advanced filtering")) return "Advanced filter setup";
  if (stageKey === "criteria") {
    return normalized.startsWith("parsing agentic criteria") ? "Screen criteria" : "Advanced filter setup";
  }

  return "";
};

export const processDisplayLabel = (message, evidenceItems = [], stageKey = "") => {
  const text = String(message || "").trim();
  if (!text) return "";

  const stageLabel = stageDisplayLabel(text, stageKey);
  if (stageLabel) return stageLabel;

  const firstLine = text.split(/\n/)[0].trim();
  if (evidenceItems.length) {
    return firstLine.split(":")[0].trim() || firstLine;
  }

  return firstLine;
};

export const processStageLabel = (message, stageKey) => {
  const stageLabel = stageDisplayLabel(message, stageKey);
  if (stageLabel) return stageLabel;

  const firstLine = String(message || "").split(/\n/)[0].trim();
  return normalizeProcessText(firstLine.split(":")[0] || firstLine);
};

const rawMessageDetailText = (message) => {
  const text = String(message || "").trim();
  if (!text) return "";

  if (text.includes(":")) {
    return text.split(":").slice(1).join(":").trim();
  }

  const lines = text.split(/\n/).map((line) => line.trim()).filter(Boolean);
  return lines.length > 1 ? lines.slice(1).join(" ") : "";
};

const textWithOptionalLimit = (value, limit) => {
  if (limit === false) return String(value || "").trim();
  return truncateText(value, limit);
};

const evidencePreviewText = (item, limit = 180) => {
  if (!item) return "";
  const value = item.value || item.detail;
  return textWithOptionalLimit([item.label, value].filter(Boolean).join(": "), limit);
};

export const compactEvidencePreviewText = (item, limit = 132) => {
  if (!item) return "";
  if (item.ticker && item.value) return textWithOptionalLimit(`${item.ticker}: ${item.value}`, limit);
  return evidencePreviewText(item, limit === false ? false : 180);
};

const compactEvidenceLine = (item) => {
  if (!item) return "";
  if (item.ticker && item.value) return `${item.ticker}: ${item.value}`;
  return [item.label, item.value || item.detail].filter(Boolean).join(": ");
};

export const processNodePreviewText = (node, limit = 360) => {
  const evidenceItems = Array.isArray(node?.evidenceItems) ? node.evidenceItems : [];
  const previewItems = evidenceItemsForPreview(evidenceItems);
  const previewCount = ["market_indices", "quotes", "stats", "direct_data"].includes(node?.stageKey) ? 5 : 3;
  const evidencePreview = previewItems
    .slice(0, previewCount)
    .map((item) => compactEvidencePreviewText(item, limit === false ? false : 132))
    .filter(Boolean)
    .join("\n");

  return textWithOptionalLimit(evidencePreview || rawMessageDetailText(node?.rawLabel || node?.message), limit);
};

export const processNodeFullPreviewText = (node) => processNodePreviewText(node, false);

export const processRecordPreviewText = (record, limit = 130) => {
  const evidenceItems = Array.isArray(record?.evidenceItems) ? record.evidenceItems : [];
  const previewItems = evidenceItemsForPreview(evidenceItems);
  if (previewItems.length) {
    const previewCount = ["market_indices", "quotes", "stats", "direct_data"].includes(record?.stageKey) ? 5 : 3;
    return compactProcessText(
      previewItems.slice(0, previewCount).map((item) => compactEvidenceLine(item)).filter(Boolean).join(" | "),
      limit,
    );
  }

  const message = String(record?.message || record?.rawLabel || "");
  return compactProcessText(rawMessageDetailText(message) || message, limit);
};

export const buildProcessNodes = (processQueue = []) => {
  const nodesByKey = new Map();

  processQueue.forEach((event, index) => {
    const payload = processEventPayload(event);
    const message = String(payload?.message || "").trim();
    if (!message) return;

    const evidenceItems = normalizeEvidenceItems(payload);
    if (!evidenceItems.length && isGenericProcessMessage(message)) return;

    const payloadStageKey = String(payload?.stage_key || payload?.stageKey || "").trim();
    const isDone = payload?.complete === true || payload?.require_completion === false;

    evidenceGroupsForStage(payloadStageKey, evidenceItems).forEach(([stageKey, groupedEvidenceItems]) => {
      const key = stageKey || normalizeLoadingPhaseText(message) || message;
      const existing = nodesByKey.get(key);
      const nextEvidenceItems = mergeEvidenceItems(existing?.evidenceItems || [], groupedEvidenceItems);

      nodesByKey.set(key, {
        key,
        label: processDisplayLabel(message, nextEvidenceItems, stageKey),
        rawLabel: message,
        status: isDone ? STATUS_DONE : STATUS_ACTIVE,
        stageKey,
        evidenceItems: nextEvidenceItems,
        firstIndex: existing?.firstIndex ?? index,
        lastIndex: index,
      });
    });
  });

  return Array.from(nodesByKey.values()).sort((a, b) => a.firstIndex - b.firstIndex);
};

export const buildProcessRecords = (events = [], doneByMsg = null) => {
  const recordsByKey = new Map();
  const usesDelayedCompletion = doneByMsg && typeof doneByMsg.get === "function";

  events.forEach((event, index) => {
    const payload = processEventPayload(event);
    const message = String(payload?.message || "").trim();
    if (!message) return;

    const evidenceItems = normalizeEvidenceItems(payload);
    if (!evidenceItems.length && isGenericProcessMessage(message)) return;

    const payloadStageKey = String(payload?.stage_key || payload?.stageKey || "").trim();
    const payloadCoverage = normalizeCoverage(payload?.coverage);
    evidenceGroupsForStage(payloadStageKey, evidenceItems).forEach(([stageKey, groupedEvidenceItems]) => {
      const key = stageKey || normalizeLoadingPhaseText(message) || message;
      const existing = recordsByKey.get(key);
      const completeOnly = payload.complete === true && !existing;
      const eventIsDone = payload.complete === true || payload.require_completion === false;
      const done = usesDelayedCompletion
        ? doneByMsg.get(message) === true || completeOnly
        : eventIsDone;
      // The event's own stage owns its coverage; items grouped under another
      // stage keep that stage's existing counts.
      const coverage = (stageKey === payloadStageKey && payloadCoverage) || existing?.coverage || null;
      const nextEvidenceItems = mergeEvidenceItems(
        existing?.evidenceItems || [],
        groupedEvidenceItems,
        coverage ? COVERAGE_EVIDENCE_LIMIT : PREVIEW_EVIDENCE_LIMIT,
        { ranked: !coverage },
      );

      recordsByKey.set(key, {
        key,
        message,
        stageKey,
        label: processStageLabel(message, stageKey),
        done: existing?.done || done,
        evidenceItems: nextEvidenceItems,
        coverage,
        firstIndex: existing?.firstIndex ?? index,
        lastIndex: index,
      });
    });
  });

  const records = Array.from(recordsByKey.values()).sort((a, b) => a.firstIndex - b.firstIndex);
  const activeIndex = records.map((record) => record.done).lastIndexOf(false);

  return records.map((record, index) => ({
    ...record,
    isActive: index === activeIndex,
  }));
};

export const visibleProcessNodesFor = (nodes, limit) => {
  if (nodes.length <= limit) return nodes;

  const latest = nodes.slice(-limit);
  const latestKeys = new Set(latest.map((node) => node.key));
  const activeOutsideWindow = nodes
    .filter((node) => node.status === STATUS_ACTIVE && !latestKeys.has(node.key))
    .sort((a, b) => b.lastIndex - a.lastIndex);

  if (!activeOutsideWindow.length) return latest;

  return [...activeOutsideWindow.slice(0, limit), ...latest]
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .slice(-limit);
};

export const isPhaseMirrorNode = (node, phaseText = "") => {
  const phase = normalizeLoadingPhaseText(phaseText);
  const rawLabel = normalizeLoadingPhaseText(node?.rawLabel || "");
  const label = normalizeLoadingPhaseText(node?.label || "");

  if (node?.stageKey === "deep_thinking") return true;
  if (!phase) return false;
  return rawLabel === phase || label === phase || rawLabel.startsWith(`${phase}:`);
};

export const isEvidenceMirroredNode = (node, mirroredEvidenceKeys) => {
  if (!mirroredEvidenceKeys?.size) return false;

  const previewItems = evidenceItemsForPreview(Array.isArray(node?.evidenceItems) ? node.evidenceItems : []);
  const previewKeys = previewItems.map((item) => evidenceItemKey(item)).filter(Boolean);
  return previewKeys.length > 0 && previewKeys.every((key) => mirroredEvidenceKeys.has(key));
};

const centerEvidencePriority = (item) => {
  const type = normalizeLoadingPhaseText(item?.type || "");
  const stageKey = normalizeLoadingPhaseText(item?.stageKey || "");
  const source = normalizeLoadingPhaseText(item?.source || "");

  if (type === "process_context" || source === "request context") return 5;
  if (type.includes("market_index") || stageKey === "market_indices") return 96;
  if (type.includes("quote") || stageKey === "quotes" || source.includes("market data")) return 100;
  if (type.includes("market_stat") || stageKey === "stats" || source.includes("market statistics")) return 92;
  if (type.includes("technical") || stageKey === "chart_context") return 88;
  if (type.includes("headline") || source.includes("news") || stageKey === "news") return 84;
  if (type.includes("filing") || source.includes("sec") || stageKey === "filings") return 80;
  if (type.includes("options") || stageKey === "options") return 76;
  if (type.includes("backtest") || type.includes("calculation") || stageKey === "direct_data") return 72;
  if (type.includes("synthesis") || type.includes("analysis_signal")) return 42;
  if (type.includes("reasoning")) return 28;
  return 50;
};

const centerEvidenceScore = (entry) => {
  const priority = centerEvidencePriority(entry.item);
  const richItemScore = evidenceItemRank(entry.item);
  const activeScore = entry.node?.status === STATUS_ACTIVE ? 10 : 0;
  const recencyScore = Math.min(Number(entry.node?.lastIndex || 0), 100);
  return priority * 1000 + richItemScore * 50 + activeScore + recencyScore;
};

export const prioritizedEvidenceItemsFor = (nodes, limit) => {
  const seen = new Set();
  const evidenceEntries = nodes.flatMap((node) =>
    (Array.isArray(node.evidenceItems) ? node.evidenceItems : []).map((item, index) => ({ item, node, index })),
  );

  return evidenceEntries
    .sort((a, b) => {
      const scoreDelta = centerEvidenceScore(b) - centerEvidenceScore(a);
      return scoreDelta || a.index - b.index;
    })
    .filter(({ item }) => {
      const key = evidenceItemKey(item) || compactEvidencePreviewText(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ item }) => item)
    .slice(0, limit);
};

export const compactTimestamp = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const dateTimeMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[2]}/${dateTimeMatch[3]} ${dateTimeMatch[4]}:${dateTimeMatch[5]}Z`;
  }

  const dateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateMatch) return `${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1]}`;

  return text;
};

export const evidenceMetaText = (item) =>
  [item.ticker, item.source, compactTimestamp(item.asOf)].filter(Boolean).join(" | ");

export const evidenceDetailText = (item, limit) => {
  const detail = String(item?.detail || "").trim();
  if (!detail || !item?.value) return "";

  if (/^(?:as of|stats updated|technicals as of|report date)\b/i.test(detail)) return "";
  return truncateText(detail, limit);
};
